import { Satellite, Database, ClipboardList, GitCompareArrows, Gauge, Workflow } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";
import { PIPELINE_STEPS, CAPABILITIES, INDIA_RELEVANCE, IMG } from "../data/content";

const ICONS = [Satellite, Database, ClipboardList, GitCompareArrows, Gauge, Workflow];

export default function Rationale() {
  return (
    <section id="rationale" data-testid="rationale-section" className="relative py-24 lg:py-32">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <SectionTag index="02" label="Mission Rationale" />
          <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-ice max-w-4xl leading-[0.95]">
            Why go to <span className="text-polar">the poles?</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 mt-14">
          <Reveal className="lg:col-span-5" delay={0.1}>
            <p className="text-base sm:text-lg text-ice/70 leading-relaxed">
              Arjuna develops capabilities for extracting environmental intelligence from satellite data, geospatial
              analytics and AI. Antarctica offers a demanding environment in which to investigate the relationship
              between remotely sensed observations and evidence collected on the ground.
            </p>
            <p className="mt-5 text-base sm:text-lg text-ice/70 leading-relaxed">
              Satellite observations and field observations complement each other — but comparing them demands care:
              spatial resolution, acquisition time, cloud cover, viewing geometry and measurement uncertainty all
              shape what a comparison can and cannot show. Field observations alone do not constitute scientific
              validation.
            </p>
            <div className="mt-8 relative clip-corner overflow-hidden">
              <img src={IMG.satEarth1} alt="Satellite view of ocean and ice texture" className="w-full h-56 sm:h-64 object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-abyss/80 to-transparent" />
              <div className="absolute bottom-3 left-3 font-mono text-[10px] tracking-[0.2em] text-polar">
                FIG.01 — EARTH FROM ORBIT · ENVIRONMENTAL TEXTURE
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PIPELINE_STEPS.map((s, i) => {
                const Icon = ICONS[i];
                return (
                  <Reveal key={s.num} delay={0.08 * i}>
                    <div
                      className="group relative bg-[#0B1F35]/80 backdrop-blur-md border border-polar/15 p-6 h-full hover:border-polar/45 transition-colors duration-300"
                      data-testid={`pipeline-step-${s.num}`}
                    >
                      <div className="flex items-start justify-between">
                        <Icon size={22} className="text-polar/80 group-hover:text-polar transition-colors" strokeWidth={1.5} />
                        <span className="font-mono text-xs text-mission tracking-widest">{s.num}</span>
                      </div>
                      <h3 className="font-display font-semibold text-xl text-ice mt-4 tracking-wide">{s.title}</h3>
                      <p className="text-sm text-ice/60 mt-2 leading-relaxed">{s.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-24 border-t border-polar/10 pt-16">
            <h3 className="font-mono text-xs uppercase tracking-[0.3em] text-polar mb-10">
              Connection to Arjuna's product capabilities — proposed demonstration, not validated products
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {CAPABILITIES.map((c, i) => (
                <Reveal key={c.title} delay={0.07 * i}>
                  <div className="border-l-2 border-mission/70 pl-5 py-1 h-full" data-testid={`capability-card-${i}`}>
                    <h4 className="font-display font-semibold text-lg text-ice tracking-wide leading-tight">{c.title}</h4>
                    <ul className="mt-4 space-y-2">
                      {c.items.map((it) => (
                        <li key={it} className="text-sm text-ice/60 leading-snug flex gap-2">
                          <span className="text-polar mt-0.5 shrink-0">—</span>
                          {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="relative clip-corner-tl overflow-hidden order-2 lg:order-1">
              <img src={IMG.iceberg} alt="Sculpted iceberg in polar waters" className="w-full h-72 sm:h-96 object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-abyss/80 to-transparent" />
              <div className="absolute bottom-3 left-3 font-mono text-[10px] tracking-[0.2em] text-polar">
                FIG.02 — POLAR ICE · CONNECTED EARTH SYSTEM
              </div>
            </div>
            <div className="order-1 lg:order-2" data-testid="india-relevance">
              <h3 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl text-ice leading-tight">
                Why Antarctica matters <span className="text-polar">to India</span>
              </h3>
              <p className="mt-5 text-base text-ice/70 leading-relaxed">
                Polar systems are connected to the wider Earth system — oceans, atmospheric circulation, sea-level
                change and climate variability. Methods developed for polar observation may, over time, prove relevant
                to Indian applications:
              </p>
              <ul className="mt-6 space-y-3">
                {INDIA_RELEVANCE.map((r) => (
                  <li key={r} className="flex gap-3 text-sm sm:text-base text-ice/75">
                    <span className="w-1.5 h-1.5 rotate-45 bg-gold mt-2 shrink-0" aria-hidden="true" />
                    {r}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs font-mono text-tech leading-relaxed tracking-wide">
                NOTE — An Antarctic expedition alone cannot validate Indian monsoon predictions or establish a direct
                causal relationship with any particular Indian climate event.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
